import { FullPageLoader } from "@/components/FullPageLoader";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ArrowLeft, BookOpen, Clock, Target, Trophy, CheckCircle2, Circle, Video, File, FileText, List, PenTool, Link as LinkIcon, Play, ChevronLeft, ChevronRight, Table as TableIcon, Image as ImageIcon, History } from "lucide-react";
import { HtmlCoursePlayer } from "@/components/learning/HtmlCoursePlayer";
import { HtmlCourseViewLoader } from "@/components/learning/HtmlCourseViewLoader";
import { ScenarioCoursePlayer } from "@/components/learning/ScenarioCoursePlayer";
import { Loader2 } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useToast } from "@/hooks/use-toast";
import { Progress } from "@/components/ui/progress";
import { ActivityModal } from "@/components/learning/ActivityModal";
import { CourseLearningLayout } from "@/components/learning/CourseLearningLayout";
import { EnhancedVideoPlayer } from "@/components/learning/EnhancedVideoPlayer";
import { ScrollRevealSection, RevealBlock } from "@/components/learning/ScrollRevealSection";
import { ModuleOverviewHero } from "@/components/learning/ModuleOverviewHero";
import { SectionContentReveal } from "@/components/learning/SectionContentReveal";
import { useSwipe } from "@/hooks/use-swipe";
import { useIsMobile } from "@/hooks/use-mobile";
import { useActivityLogger } from "@/hooks/use-activity-logger";
import { useTimeTracker } from "@/hooks/use-time-tracker";
import { createSafeHtml } from "@/lib/sanitize";
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { useUserRole } from "@/hooks/use-user-role";
import { invokeFunction } from "@/lib/invokeFunction";

const getCurrentSessionUser = async () => {
  const { data, error } = await supabase.auth.getSession();
  if (error) {
    console.warn("[CourseViewPage] Unable to read local auth session", error);
    return null;
  }
  return data.session?.user ?? null;
};

export default function CourseViewPage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const isNative = Capacitor.isNativePlatform();
  const exitPath = isNative ? "/app/courses" : "/learning/courses";
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const { logActivity } = useActivityLogger();
  const {
    userId: courseViewerId,
    isAdmin,
    isMasterAdmin,
    isLoading: isRoleLoading,
  } = useUserRole();
  const canBypassAccessGate = isAdmin || isMasterAdmin;
  const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
  const [currentSubPageIndex, setCurrentSubPageIndex] = useState(0);
  const [completedModules, setCompletedModules] = useState<Set<string>>(new Set());
  const [selectedActivity, setSelectedActivity] = useState<any>(null);
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);
  const [localCompletedActivities, setLocalCompletedActivities] = useState<Set<string>>(new Set());
  const [htmlJustCompleted, setHtmlJustCompleted] = useState(false);
  const [autoEnrolError, setAutoEnrolError] = useState<string | null>(null);
  const completionModeRef = useRef<"html" | "module">("module");

  useEffect(() => {
    if (!isNative) return;
    const subPromise = CapApp.addListener("backButton", () => {
      navigate("/app/courses");
    });
    return () => {
      subPromise.then((s) => s.remove()).catch(() => {});
    };
  }, [isNative, navigate]);

  // Disable right-click in course player
  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
    };
    document.addEventListener('contextmenu', handleContextMenu);
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);
  const { data: course, isLoading, error: courseLoadError } = useQuery({
    queryKey: ["course-view", courseId, courseViewerId, canBypassAccessGate],
    retry: (failureCount, error: any) => {
      if (failureCount >= 3) return false;
      return error?.message !== "Course not found";
    },
    staleTime: 60 * 1000,
    queryFn: async () => {
      if (!courseId) throw new Error("No course ID provided");

      // Step 1: fetch the light course shell + local session first.
      // Do NOT fetch modules/activities before the access gate is known: users
      // assigned via learning paths may not have an enrolment row yet, and the
      // content-table RLS can hang long enough to leave the launch page stuck.
      const [courseResult, userResult] = await Promise.all([
        supabase.from("courses").select("id, title, description, cover_image, status, is_mandatory, category, prerequisite_course_id, version, created_at, course_format, scenario_data, superseded_by, self_enrollable, project_id").eq("id", courseId).maybeSingle(),
        getCurrentSessionUser(),
      ]);

      if (courseResult.error) throw courseResult.error;
      if (!courseResult.data) throw new Error("Course not found");

      const courseData = courseResult.data;
      const user = userResult;

      // Step 2: fetch enrolment and LP coverage before touching content tables.
      // NOTE: We cannot PostgREST-embed learning_path_courses -> user_learning_paths — there
      // is no FK between them (both FK to learning_paths). So we fetch the user's LP ids
      // first, then check learning_path_courses for this course scoped to those ids.
      const [enrollmentResult, completionsResult, userLpResult] = await Promise.all([
        user
          ? supabase.from("course_enrollments").select("id, course_id, user_id, progress, completed, completed_at, completed_module_ids").eq("course_id", courseId).eq("user_id", user.id).maybeSingle()
          : Promise.resolve({ data: null }),
        user
          ? (supabase.from("activity_completions") as any).select("activity_id").eq("course_id", courseId).eq("user_id", user.id)
          : Promise.resolve({ data: [] }),
        user
          ? supabase.from("user_learning_paths").select("learning_path_id").eq("user_id", user.id)
          : Promise.resolve({ data: [] }),
      ]);

      if ((enrollmentResult as any)?.error) throw (enrollmentResult as any).error;
      if ((completionsResult as any)?.error) throw (completionsResult as any).error;
      if ((userLpResult as any)?.error) throw (userLpResult as any).error;

      const userLpIds = Array.isArray(userLpResult?.data)
        ? (userLpResult.data as any[]).map((r) => r.learning_path_id).filter(Boolean)
        : [];
      let lpMembership: any[] = [];
      if (user && userLpIds.length > 0) {
        const { data: lpcData } = await supabase
          .from("learning_path_courses")
          .select("learning_path_id")
          .eq("course_id", courseId)
          .in("learning_path_id", userLpIds)
          .limit(1);
        lpMembership = lpcData || [];
      }

      const hasEnrollmentForContent = !!enrollmentResult?.data;
      const inAssignedLearningPath = lpMembership.length > 0;

      if (!hasEnrollmentForContent && inAssignedLearningPath && !canBypassAccessGate) {
        return {
          ...courseData,
          pages: [],
          modules: [],
          enrollment: null,
          completions: completionsResult?.data || [],
          inAssignedLearningPath,
          currentUserId: user?.id ?? null,
        };
      }

      if (!hasEnrollmentForContent && !inAssignedLearningPath && !canBypassAccessGate) {
        return {
          ...courseData,
          pages: [],
          modules: [],
          enrollment: null,
          completions: completionsResult?.data || [],
          inAssignedLearningPath,
          currentUserId: user?.id ?? null,
        };
      }

      // Step 3: content is now allowed to load. Use the authenticated edge
      // function so the viewer does not wait on content-table RLS chains for
      // large HTML courses; the function repeats the same access checks with
      // service-role reads after access is confirmed.
      const { data: contentData, error: contentError } = await invokeFunction<{
        pages: any[];
        modules: any[];
      }>("get-course-view-content", {
        body: { courseId },
      });

      if (contentError) throw contentError;

      const pagesData = contentData?.pages || [];
      const modulesWithContent = contentData?.modules || [];

      return {
        ...courseData,
        pages: pagesData,
        modules: modulesWithContent,
        enrollment: enrollmentResult?.data || null,
        completions: completionsResult?.data || [],
        inAssignedLearningPath,
        currentUserId: user?.id ?? null,
      };
    },
    enabled: !!courseId && !isRoleLoading
  });

  // V1 users who completed the course can still access it for review.
  // V2 is a separate enrollment — no auto-redirect needed.

  const completions = course?.completions || [];

  // Each module is a page
  const modules = course?.modules || [];
  const currentModule = modules[currentModuleIndex];
  const totalPages = modules.length;
  
  // Calculate total sub-pages in current module (1 overview + sections + module activities)
  const getTotalSubPages = (module: any) => {
    if (!module) return 1;
    const sectionsCount = module.sections?.length || 0;
    const moduleActivitiesCount = module.moduleActivities?.length || 0;
    return 1 + sectionsCount + moduleActivitiesCount; // 1 for overview
  };
  
  const totalSubPages = getTotalSubPages(currentModule);
  const isFirstSubPage = currentSubPageIndex === 0;
  const isLastSubPage = currentSubPageIndex === totalSubPages - 1;
  
  // Calculate completion percentage based on completed modules/pages
  const completionPercentage = totalPages > 0 
    ? Math.round((completedModules.size / totalPages) * 100) 
    : 0;

  // Mobile swipe navigation for modules (only on mobile)
  useSwipe({
    onSwipeLeft: () => {
      if (isMobile && currentModuleIndex < totalPages - 1) {
        handleNextPage();
      }
    },
    onSwipeRight: () => {
      if (isMobile && currentModuleIndex > 0) {
        handlePreviousPage();
      }
    }
  }, 75);

  const completeCourse = useMutation({
    mutationFn: async () => {
      const user = await getCurrentSessionUser();
      if (!user) throw new Error("Not authenticated");

      // Get user profile for name
      const { data: profile } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", user.id)
        .maybeSingle();

      // Check if enrollment exists
      const { data: existingEnrollment } = await supabase
        .from("course_enrollments")
        .select("id, completed")
        .eq("course_id", courseId)
        .eq("user_id", user.id)
        .maybeSingle();

      // Mark enrolment complete (idempotent) BEFORE issuing the certificate.
      // We only issue a certificate if this step actually succeeds, so the
      // two states can never disagree (no more "cert issued but progress 99%").
      // Enrolment row is guaranteed to exist here: the access gate creates
      // or requires it before content ever renders.
      if (!existingEnrollment) {
        throw new Error("No enrolment exists for this course.");
      }
      const { error } = await supabase
        .from("course_enrollments")
        .update({
          progress: 100,
          completed: true,
          completed_at: new Date().toISOString(),
        })
        .eq("id", existingEnrollment.id)
        .select();

      if (error) throw error;

      // Only now create the certificate
      const { error: certError } = await supabase
        .from("certificates")
        .insert({
          user_id: user.id,
          course_id: courseId,
          course_title: course?.title || "Course",
          user_name: profile?.full_name || user.email || "Student",
          completion_date: new Date().toISOString()
        });

      if (certError && certError.code !== '23505') { // Ignore duplicate key error
        throw certError;
      }
    },
    onSuccess: () => {
      toast({
        title: "Congratulations!",
        description: "You have completed this course and earned a certificate!",
        variant: "success"
      });
      queryClient.invalidateQueries({ queryKey: ["course-view", courseId] });
      queryClient.invalidateQueries({ queryKey: ["certificates"] });
      queryClient.invalidateQueries({ queryKey: ["my-progress"] });
      if (completionModeRef.current === "html") {
        // HTML iframe player shows its own success overlay with a View Certificate CTA
        setHtmlJustCompleted(true);
      } else {
        navigate("/learning/my-progress");
      }
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // -------- Access gate --------
  // A course_enrollments row is required before any content is rendered
  // or progress is written. Three admitted paths:
  //   (a) enrolment already exists → render.
  //   (b) no enrolment but course is in one of the user's assigned LPs →
  //       auto-create the enrolment (user_id = self, assigned_by NULL) and render.
  //   (c) no enrolment, not in an LP, course.self_enrollable = true → render
  //       a landing card with an explicit "Enrol in this course" CTA.
  //   otherwise → show a "This course requires an assignment" state.
  const hasEnrollment = !!course?.enrollment;
  const inAssignedLp = !!(course as any)?.inAssignedLearningPath;
  const isSelfEnrollable = !!(course as any)?.self_enrollable;
  const currentUserIdFromQuery: string | null = (course as any)?.currentUserId ?? null;
  const courseProjectId: string | null = (course as any)?.project_id ?? null;

  const enrolMutation = useMutation({
    mutationFn: async () => {
      if (!currentUserIdFromQuery || !courseId) throw new Error("Not authenticated");
      if (!courseProjectId) throw new Error("Course project is missing");
      const { error } = await supabase
        .from("course_enrollments")
        .insert({
          course_id: courseId,
          user_id: currentUserIdFromQuery,
          project_id: courseProjectId,
          progress: 0,
          completed: false,
        })
        .select();
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["course-view", courseId] });
      queryClient.invalidateQueries({ queryKey: ["my-progress"] });
      queryClient.invalidateQueries({ queryKey: ["user-course-enrollments"] });
    },
    onError: (error: any) => {
      toast({
        title: "Could not enrol",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  // (b) auto-create enrolment for users whose LP already covers this course.
  const autoEnrolAttempted = useRef(false);
  useEffect(() => {
    if (autoEnrolAttempted.current) return;
    if (!course || hasEnrollment) return;
    if (canBypassAccessGate) return; // admins view without creating an enrolment row
    if (!inAssignedLp) return;
    if (!currentUserIdFromQuery || !courseId || !courseProjectId) return;
    autoEnrolAttempted.current = true;
    (async () => {
      const { error } = await supabase
        .from("course_enrollments")
        .insert({
          course_id: courseId,
          user_id: currentUserIdFromQuery,
          project_id: courseProjectId,
          progress: 0,
          completed: false,
        })
        .select();
      if (!error || error.code === "23505") {
        setAutoEnrolError(null);
        queryClient.invalidateQueries({ queryKey: ["course-view", courseId] });
        queryClient.invalidateQueries({ queryKey: ["my-progress"] });
      } else {
        setAutoEnrolError(error.message || "Could not prepare your course.");
      }
    })();
  }, [course, hasEnrollment, inAssignedLp, canBypassAccessGate, currentUserIdFromQuery, courseId, courseProjectId, queryClient]);


  const handleModuleComplete = async () => {
    if (!currentModule) return;
    
    const newCompletedModules = new Set([...completedModules, currentModule.id]);
    setCompletedModules(newCompletedModules);
    
    // Save to database
    const user = await getCurrentSessionUser();
    if (!user) return;
    
    const completedModuleIds = Array.from(newCompletedModules);
    const progress = Math.round((completedModuleIds.length / totalPages) * 100);
    
    // Check if enrollment exists
    const { data: existingEnrollment } = await supabase
      .from("course_enrollments")
      .select("id")
      .eq("course_id", courseId)
      .eq("user_id", user.id)
      .maybeSingle();

    if (!existingEnrollment) {
      // Access gate guarantees enrolment exists before content renders.
      // If it doesn't, silently skip — do NOT insert (RLS-controlled).
      return;
    }
    await supabase
      .from("course_enrollments")
      .update({
        completed_module_ids: completedModuleIds,
        progress: progress
      })
      .eq("id", existingEnrollment.id);
    
    queryClient.invalidateQueries({ queryKey: ["course-view", courseId] });
    queryClient.invalidateQueries({ queryKey: ["my-progress"] });
  };

  const handleNextPage = () => {
    if (currentSubPageIndex < totalSubPages - 1) {
      // Move to next sub-page within same module
      setCurrentSubPageIndex(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (currentModuleIndex < totalPages - 1) {
      // Move to next module
      handleModuleComplete();
      setCurrentModuleIndex(prev => prev + 1);
      setCurrentSubPageIndex(0);
      setLocalCompletedActivities(new Set());
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePreviousPage = () => {
    if (currentSubPageIndex > 0) {
      // Move to previous sub-page within same module
      setCurrentSubPageIndex(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (currentModuleIndex > 0) {
      // Move to previous module (last sub-page)
      const prevModule = modules[currentModuleIndex - 1];
      const prevTotalSubPages = getTotalSubPages(prevModule);
      setCurrentModuleIndex(prev => prev - 1);
      setCurrentSubPageIndex(prevTotalSubPages - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleCompleteCourse = () => {
    handleModuleComplete();
    completeCourse.mutate();
  };

  // Load completed modules from enrollment
  useEffect(() => {
    if (course?.enrollment?.completed_module_ids) {
      setCompletedModules(new Set(course.enrollment.completed_module_ids));
    } else {
      setCompletedModules(new Set());
    }
  }, [course]);

  // Log course view activity
  useEffect(() => {
    if (course && courseId) {
      logActivity({
        activityType: "course",
        entityType: "course",
        entityId: courseId,
        entityTitle: course.title,
        action: "viewed"
      });
    }
  }, [course, courseId, logActivity]);

  // Track time spent on current module
  useTimeTracker({
    moduleId: currentModule?.id || "",
    courseId: courseId || "",
    enabled: !!currentModule && !!courseId
  });

  // Log module navigation
  useEffect(() => {
    if (currentModule && courseId) {
      logActivity({
        activityType: "module",
        entityType: "module",
        entityId: currentModule.id,
        entityTitle: currentModule.title,
        action: "viewed"
      });
    }
  }, [currentModule, courseId, logActivity]);

  const handleActivityClick = (activity: any) => {
    setSelectedActivity(activity);
    setIsActivityModalOpen(true);
  };

  const handleActivityNavigate = (activityId: string) => {
    const allActivities = getAllActivities();
    const activity = allActivities.find((a: any) => a.id === activityId);
    if (activity) {
      setSelectedActivity(activity);
    }
  };

  const getAllActivities = () => {
    if (!currentModule) return [];
    const sectionActivities = currentModule.sections?.reduce((acc: any[], section: any) => {
      return [...acc, ...(section.activities || [])];
    }, []) || [];
    const moduleActivities = currentModule.moduleActivities || [];
    return [...sectionActivities, ...moduleActivities];
  };

  const isActivityCompleted = (activityId: string) => {
    return completions?.some(c => c.activity_id === activityId) || false;
  };

  // Unified handler for any activity completion (quiz, video, manual mark complete)
  const handleActivityComplete = (activityId: string) => {
    setLocalCompletedActivities(prev => new Set([...prev, activityId]));
  };

  const handleQuizComplete = (activityId: string) => handleActivityComplete(activityId);
  const handleVideoComplete = (activityId: string) => handleActivityComplete(activityId);

  // Get activities for the current sub-page only
  const getCurrentSubPageActivities = (): any[] => {
    if (!currentModule || currentSubPageIndex === 0) return []; // Overview page has no activities
    
    const sectionsCount = currentModule.sections?.length || 0;
    const sectionIndex = currentSubPageIndex - 1;
    
    if (sectionIndex < sectionsCount) {
      // Current sub-page is a section
      return currentModule.sections?.[sectionIndex]?.activities || [];
    }
    
    // Current sub-page is a module-level activity
    const activityIndex = currentSubPageIndex - 1 - sectionsCount;
    const moduleActivity = currentModule.moduleActivities?.[activityIndex];
    return moduleActivity ? [moduleActivity] : [];
  };

  // Check if current sub-page has incomplete required activities
  const hasIncompleteRequiredActivities = () => {
    const activities = getCurrentSubPageActivities();
    const requiredActivities = activities.filter((a: any) => a.is_required);
    
    if (requiredActivities.length === 0) return false;
    
    // Activity types that are passive (no explicit completion action) should not block navigation
    const passiveTypes = ['text', 'document', 'link', 'image', 'table', 'slides', 'flowchart'];
    
    return requiredActivities.some((activity: any) => {
      // Skip passive activities - they have no built-in completion mechanism
      if (passiveTypes.includes(activity.activity_type)) return false;
      // Skip video activities that have no content (no video to watch)
      if (activity.activity_type === 'video') {
        const hasVideo = activity.content?.videoUrl || activity.content?.youtubeUrl;
        if (!hasVideo) return false;
      }
      return !isActivityCompleted(activity.id) && !localCompletedActivities.has(activity.id);
    });
  };

  const isNextDisabled = hasIncompleteRequiredActivities();

  const getActivityIcon = (type: string) => {
    switch (type) {
      case "video": return Video;
      case "document": return File;
      case "text": return FileText;
      case "quiz": return List;
      case "assignment": return PenTool;
      case "link": return LinkIcon;
      case "table": return TableIcon;
      case "image": return ImageIcon;
      default: return Circle;
    }
  };

  const totalActivities = currentModule?.sections?.reduce((sum: number, s: any) => 
    sum + (s.activities?.length || 0), 0) || 0;

  const totalDuration = currentModule?.sections?.reduce((sectionSum: number, s: any) =>
    sectionSum + s.activities?.reduce((actSum: number, a: any) =>
      actSum + (a.duration_minutes || 0), 0), 0) || 0;

  if (isRoleLoading || isLoading) {
    return <FullPageLoader message="Loading course..." />;
  }

  if (courseLoadError) {
    return (
      <div className="min-h-screen bg-gradient-subtle p-6">
        <div className="max-w-5xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate(exitPath)}
            className="mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Courses
          </Button>
          <Card>
            <CardContent className="p-6 text-center space-y-2">
              <p className="text-destructive">Unable to load course</p>
              <p className="text-sm text-muted-foreground">
                {courseLoadError instanceof Error
                  ? courseLoadError.message
                  : "Please refresh and try again."}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!course) {
    // Don't show ambiguous "Course not found" — keep showing the loader while
    // the query settles. Real failures surface via courseLoadError above.
    return (
      <FullPageLoader />
    );
  }

  // Access gate: no enrolment, no LP coverage → either offer self-enrol or block.
  // Admins/master admins bypass the gate entirely: they can view any course
  // without an enrolment row being created or auto-inserted.
  if (!hasEnrollment && !inAssignedLp && !canBypassAccessGate) {
    return (
      <div className="min-h-screen bg-gradient-subtle p-6">
        <div className="max-w-3xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate(exitPath)}
            className="mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Courses
          </Button>
          <Card>
            <CardHeader>
              <CardTitle>{course.title}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {course.description && (
                <p className="text-muted-foreground">{course.description}</p>
              )}
              {isSelfEnrollable ? (
                <>
                  <p className="text-sm text-muted-foreground">
                    This course is open for self-enrolment. Enrol below to begin.
                  </p>
                  <Button
                    onClick={() => enrolMutation.mutate()}
                    disabled={enrolMutation.isPending}
                  >
                    {enrolMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Enrolling…
                      </>
                    ) : (
                      "Enrol in this course"
                    )}
                  </Button>
                </>
              ) : (
                <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                  This course requires an assignment. Please contact your administrator or ask to be added to the relevant learning path.
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // If we're waiting for the auto-enrol effect to finish, show a loader
  // rather than briefly rendering content without an enrolment row.
  if (!hasEnrollment && inAssignedLp && !canBypassAccessGate) {
    if (autoEnrolError) {
      return (
        <div className="min-h-screen bg-gradient-subtle p-6">
          <div className="max-w-3xl mx-auto">
            <Button
              variant="ghost"
              onClick={() => navigate(exitPath)}
              className="mb-6"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Courses
            </Button>
            <Card>
              <CardContent className="p-6 text-center space-y-2">
                <p className="text-destructive">Unable to prepare course</p>
                <p className="text-sm text-muted-foreground">{autoEnrolError}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      );
    }
    return <FullPageLoader message="Preparing your course..." />;
  }



  // Handle module selection from sidebar
  const handleModuleSelect = (index: number) => {
    if (index !== currentModuleIndex) {
      // Complete current module if moving forward
      if (index > currentModuleIndex) {
        handleModuleComplete();
      }
      setCurrentModuleIndex(index);
      setCurrentSubPageIndex(0);
      setLocalCompletedActivities(new Set());
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Handle sub-page selection from sidebar
  const handleSubPageSelect = (moduleIndex: number, subPageIndex: number) => {
    if (moduleIndex !== currentModuleIndex) {
      if (moduleIndex > currentModuleIndex) {
        handleModuleComplete();
      }
      setCurrentModuleIndex(moduleIndex);
    }
    setCurrentSubPageIndex(subPageIndex);
    setLocalCompletedActivities(new Set());
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Get current section based on subPageIndex
  const getCurrentSection = () => {
    if (!currentModule || currentSubPageIndex === 0) return null;
    const sectionIndex = currentSubPageIndex - 1;
    if (sectionIndex < (currentModule.sections?.length || 0)) {
      return currentModule.sections?.[sectionIndex];
    }
    return null;
  };

  // Get current module activity based on subPageIndex  
  const getCurrentModuleActivity = () => {
    if (!currentModule) return null;
    const sectionsCount = currentModule.sections?.length || 0;
    const activityIndex = currentSubPageIndex - 1 - sectionsCount;
    if (activityIndex >= 0 && activityIndex < (currentModule.moduleActivities?.length || 0)) {
      return currentModule.moduleActivities?.[activityIndex];
    }
    return null;
  };

  const currentSection = getCurrentSection();
  const currentModuleActivity = getCurrentModuleActivity();

  // Detect if this is an iframe-mode HTML course
  const isHtmlIframeCourse = (() => {
    if (!course || modules.length === 0) return false;
    // Check all activities across all modules/sections for a single html_upload iframe activity
    const allActivities: any[] = [];
    for (const m of modules) {
      for (const s of (m.sections || [])) {
        for (const a of (s.activities || [])) {
          allActivities.push(a);
        }
      }
      for (const a of (m.moduleActivities || [])) {
        allActivities.push(a);
      }
    }
    if (allActivities.length === 1 && allActivities[0].activity_type === 'html_upload') {
      const content = allActivities[0].content as Record<string, unknown> | null;
      if (content?.mode !== 'iframe') return false;
      const hasInline = typeof content?.rawHtml === 'string' && (content.rawHtml as string).length > 0;
      const hasStorageKey = typeof content?.storageKey === 'string' && (content.storageKey as string).length > 0;
      return hasInline || hasStorageKey;
    }
    return false;
  })();

  if (isHtmlIframeCourse) {
    // Find the single html_upload activity
    let htmlActivity: any = null;
    for (const m of modules) {
      for (const s of (m.sections || [])) {
        for (const a of (s.activities || [])) {
          if (a.activity_type === 'html_upload') htmlActivity = a;
        }
      }
      for (const a of (m.moduleActivities || [])) {
        if (a.activity_type === 'html_upload') htmlActivity = a;
      }
    }
    const content = htmlActivity?.content as Record<string, unknown>;

    // Parse resume state from enrollment.completed_module_ids — for HTML iframe courses we
    // stash visited page numbers as strings like "html-page-3" alongside any module IDs.
    const storedIds: string[] = Array.isArray(course.enrollment?.completed_module_ids)
      ? (course.enrollment!.completed_module_ids as unknown as string[])
      : [];
    const initialVisitedPages = storedIds
      .filter((s) => typeof s === "string" && s.startsWith("html-page-"))
      .map((s) => parseInt(s.replace("html-page-", ""), 10))
      .filter((n) => Number.isFinite(n) && n > 0);
    const initialCurrentPage = storedIds
      .filter((s) => typeof s === "string" && s.startsWith("html-current-page-"))
      .map((s) => parseInt(s.replace("html-current-page-", ""), 10))
      .find((n) => Number.isFinite(n) && n > 0);
    const initialMaxPageReached = initialVisitedPages.length
      ? Math.max(...initialVisitedPages)
      : 1;
    const initialScrollProgress = course.enrollment?.progress ?? 0;

    return (
      <HtmlCourseViewLoader
        activityId={htmlActivity?.id}
        inlineRawHtml={content?.rawHtml as string | undefined}
        storageKey={content?.storageKey as string | undefined}
        courseTitle={course.title}
        courseId={courseId || ''}
        onComplete={() => {
          completionModeRef.current = "html";
          completeCourse.mutate();
        }}
        isCompleting={completeCourse.isPending}
        isAlreadyCompleted={course.enrollment?.completed === true}
        initialVisitedPages={initialVisitedPages}
        initialCurrentPage={initialCurrentPage}
        initialMaxPageReached={initialMaxPageReached}
        initialScrollProgress={initialScrollProgress}
        justCompleted={htmlJustCompleted}
        onViewCertificate={() => navigate(isNative ? "/app/courses" : `/learning/certificates?highlight=${courseId}`)}
        onProgressChange={async (state) => {
          const user = await getCurrentSessionUser();
          if (!user || !courseId) return;
          // Merge: preserve any non-html ids that may already exist; replace all HTML resume markers.
          const isHtmlProgressMarker = (s: unknown) =>
            typeof s === "string" &&
            (s.startsWith("html-page-") ||
              s.startsWith("html-current-page-") ||
              s.startsWith("html-total-pages-"));
          const preservedIds = storedIds.filter((s) => !isHtmlProgressMarker(s));
          const newIds = [
            ...preservedIds,
            ...state.visitedPages.map((p) => `html-page-${p}`),
            `html-current-page-${state.currentPage}`,
            ...(state.totalPages > 0 ? [`html-total-pages-${state.totalPages}`] : []),
          ];

          // Re-read latest enrolment state so we never downgrade a completed course
          // back to in-progress (the iframe keeps emitting progress events after
          // the certificate has already been issued).
          const { data: existing } = await supabase
            .from("course_enrollments")
            .select("id, completed, progress")
            .eq("course_id", courseId)
            .eq("user_id", user.id)
            .maybeSingle();

          if (existing?.completed) {
            // Already completed — only persist visited-page markers, never touch progress/completed.
            await supabase
              .from("course_enrollments")
              .update({ completed_module_ids: newIds })
              .eq("id", existing.id)
              .select();
            return;
          }

          if (!existing) {
            // Access gate guarantees enrolment exists; if it doesn't,
            // do NOT insert (RLS-controlled).
            return;
          }
          // Never allow a late iframe progress event to drop a course below
          // its previously-persisted %. This prevents the 100 → 99 race where
          // "Complete Course" writes 100 and a trailing page-info emit
          // afterwards recomputes 99 and overwrites it.
          const safeProgress = Math.max(state.progressPercent, existing?.progress ?? 0);
          await supabase
            .from("course_enrollments")
            .update({
              progress: safeProgress,
              completed_module_ids: newIds,
            })
            .eq("id", existing.id)
            .select();
          queryClient.invalidateQueries({ queryKey: ["my-progress"] });
        }}
      />
    );
  }

  // Detect scenario_quiz format courses
  if (course.course_format === 'scenario_quiz' && course.scenario_data) {
    const scenarioData = course.scenario_data as any;
    const scenarioConfig = scenarioData.config || {
      title: course.title,
      subtitle: course.description || '',
      organization: '',
      passMark: 80,
      version: course.version || 'V1',
    };
    const scenarioModules = scenarioData.modules || [];
    
    return (
      <ScenarioCoursePlayer
        courseId={courseId || ''}
        courseTitle={course.title}
        config={scenarioConfig}
        modules={scenarioModules}
        isAlreadyCompleted={course.enrollment?.completed === true}
      />
    );
  }

  return (
    <CourseLearningLayout
      courseTitle={course.title}
      courseId={courseId || ''}
      modules={modules.map((m: any) => ({ 
        id: m.id, 
        title: m.title,
        description: m.description,
        order_index: m.order_index,
        sections: m.sections?.map((s: any) => ({
          id: s.id,
          title: s.title,
          description: s.description,
          image_url: s.image_url,
          activities: s.activities?.map((a: any) => ({
            id: a.id,
            title: a.title,
            activity_type: a.activity_type,
            duration_minutes: a.duration_minutes,
            content: a.content,
            description: a.description,
            is_required: a.is_required
          })) || []
        })) || [],
        moduleActivities: m.moduleActivities?.map((a: any) => ({
          id: a.id,
          title: a.title,
          activity_type: a.activity_type,
          duration_minutes: a.duration_minutes,
          content: a.content,
          description: a.description,
          is_required: a.is_required
        })) || []
      }))}
      currentModuleIndex={currentModuleIndex}
      currentSubPageIndex={currentSubPageIndex}
      completedModules={completedModules}
      completionPercentage={completionPercentage}
      onModuleSelect={handleModuleSelect}
      onSubPageSelect={handleSubPageSelect}
      onPrevious={handlePreviousPage}
      onNext={handleNextPage}
      nextDisabled={isNextDisabled}
      hasUnsavedProgress={true}
      isFirstSubPage={isFirstSubPage}
      isLastSubPage={isLastSubPage}
      isLastPage={isLastSubPage && currentModuleIndex === totalPages - 1}
    >
      {/* Course Content - Current Sub-Page */}
      {totalPages === 0 ? (
        <Card className="mb-6">
          <CardContent className="p-6 sm:p-8">
            <div className="text-center py-12">
              <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">This course has no modules yet.</p>
              <p className="text-sm text-muted-foreground mt-2">Please check back later or contact support.</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="mb-6">
          <CardHeader className="p-3 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <CardTitle className="flex flex-wrap items-center gap-2 text-base sm:text-lg">
                {currentModule?.title || `Module ${currentModuleIndex + 1}`}
                <Badge variant="outline" className="text-xs">
                  {currentSubPageIndex + 1}/{totalSubPages}
                </Badge>
              </CardTitle>
              {completedModules.has(currentModule?.id) && (
                <Badge variant="default" className="bg-green-500 w-fit">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Completed
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4 sm:space-y-6 p-3 sm:p-6 pt-0 sm:pt-0">
            {!currentModule ? (
              <div className="text-center py-12">
                <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No content available for this module</p>
              </div>
            ) : currentSubPageIndex === 0 ? (
              /* Module Overview Page - Scroll Reveal Hero */
              <ModuleOverviewHero
                title={currentModule.title}
                description={currentModule.description}
                sections={currentModule.sections || []}
                moduleActivities={currentModule.moduleActivities || []}
                moduleNumber={currentModuleIndex + 1}
              />
            ) : currentSection ? (
              /* Section Page - Scroll Reveal */
              <SectionContentReveal
                section={currentSection}
                sectionNumber={currentSubPageIndex}
                completedVideos={localCompletedActivities}
                onVideoComplete={handleVideoComplete}
                onActivityClick={handleActivityClick}
                isActivityCompleted={isActivityCompleted}
                getActivityIcon={getActivityIcon}
              />
            ) : currentModuleActivity ? (
              /* Module Activity Page with Reveal Animation */
              <div className="space-y-6 py-4">
                <RevealBlock>
                  <div className="relative">
                    <div className="flex items-start gap-4">
                      <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20 shrink-0">
                        {getActivityIcon(currentModuleActivity.activity_type) === List ? (
                          <List className="h-6 w-6 text-primary" />
                        ) : (
                          <FileText className="h-6 w-6 text-primary" />
                        )}
                      </div>
                      <div className="flex-1 space-y-2">
                        <h2 className="text-xl sm:text-2xl font-semibold text-foreground leading-tight">
                          {currentModuleActivity.title}
                        </h2>
                        {currentModuleActivity.description && (
                          <p className="text-muted-foreground">{currentModuleActivity.description}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </RevealBlock>

                <RevealBlock delay={150}>
                  <div 
                    onClick={() => handleActivityClick(currentModuleActivity)}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-5 sm:p-6 rounded-xl bg-gradient-to-br from-primary/10 to-transparent border border-primary/20 hover:border-primary/40 hover:shadow-lg transition-all duration-300 group cursor-pointer gap-4"
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      {isActivityCompleted(currentModuleActivity.id) ? (
                        <div className="p-3 rounded-xl bg-green-500/10">
                          <CheckCircle2 className="h-6 w-6 text-green-500" />
                        </div>
                      ) : (
                        <div className="p-3 rounded-xl bg-primary/10">
                          <Circle className="h-6 w-6 text-primary" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-lg group-hover:text-primary transition-colors truncate">
                          {isActivityCompleted(currentModuleActivity.id) ? 'Review' : 'Start'} {currentModuleActivity.title}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Click to {isActivityCompleted(currentModuleActivity.id) ? 'review' : 'begin'} this activity
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 ml-14 sm:ml-0">
                      {currentModuleActivity.duration_minutes && (
                        <Badge variant="secondary" className="text-sm px-3 py-1">
                          <Clock className="h-4 w-4 mr-1" />
                          {currentModuleActivity.duration_minutes}m
                        </Badge>
                      )}
                      <Button 
                        size="default" 
                        className="opacity-80 group-hover:opacity-100 transition-opacity"
                      >
                        <Play className="h-4 w-4 mr-2" />
                        {isActivityCompleted(currentModuleActivity.id) ? 'Review' : 'Start'}
                      </Button>
                    </div>
                  </div>
                </RevealBlock>

                <RevealBlock delay={250}>
                  <div className="flex items-center gap-3 pt-4">
                    <div className="flex-1 h-px bg-gradient-to-r from-border via-primary/30 to-border" />
                    <CheckCircle2 className="h-4 w-4 text-primary/50" />
                    <div className="flex-1 h-px bg-gradient-to-r from-border via-primary/30 to-border" />
                  </div>
                </RevealBlock>
              </div>
            ) : (
              <div className="text-center py-12">
                <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No content available</p>
              </div>
            )}

            {/* Navigation Controls */}
            <div className="flex items-center justify-between pt-4 sm:pt-6 border-t gap-2">
              <Button
                variant="outline"
                onClick={handlePreviousPage}
                disabled={isFirstSubPage && currentModuleIndex === 0}
                size={isMobile ? "sm" : "default"}
                className="text-xs sm:text-sm"
              >
                <ChevronLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Previous</span>
                <span className="sm:hidden">Prev</span>
              </Button>
              
              {isLastSubPage && currentModuleIndex === totalPages - 1 ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={handleCompleteCourse}
                        disabled={completeCourse.isPending || isNextDisabled}
                        className="bg-gradient-primary text-xs sm:text-sm"
                        size={isMobile ? "sm" : "default"}
                      >
                        <Trophy className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                        <span className="hidden sm:inline">{completeCourse.isPending ? "Completing..." : "Complete Course"}</span>
                        <span className="sm:hidden">{completeCourse.isPending ? "..." : "Complete"}</span>
                      </Button>
                    </TooltipTrigger>
                    {isNextDisabled && !completeCourse.isPending && (
                      <TooltipContent>
                        <p>Complete all required activities to continue</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={handleNextPage}
                        disabled={isNextDisabled}
                        className="bg-gradient-primary text-xs sm:text-sm"
                        size={isMobile ? "sm" : "default"}
                      >
                        <span className="hidden sm:inline">Next</span>
                        <span className="sm:hidden">Next</span>
                        <ChevronRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 ml-1 sm:ml-2" />
                      </Button>
                    </TooltipTrigger>
                    {isNextDisabled && (
                      <TooltipContent>
                        <p>Complete all required activities to continue</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </CardContent>
        </Card>
      )}
        {/* Activity Modal */}
        {selectedActivity && (
          <ActivityModal
            isOpen={isActivityModalOpen}
            onClose={() => {
              setIsActivityModalOpen(false);
              setSelectedActivity(null);
            }}
            activity={selectedActivity}
            courseId={courseId || ''}
            allActivities={getAllActivities()}
            onNavigate={handleActivityNavigate}
            onQuizComplete={handleQuizComplete}
            onVideoComplete={handleVideoComplete}
            onActivityComplete={handleActivityComplete}
          />
        )}
    </CourseLearningLayout>
  );
}
